import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Focus, LocateFixed, Maximize2, Minimize2 } from "lucide-react";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
mapboxgl.accessToken = TOKEN;

const STATUS_COLORS = {
  open: "#1C1B1A", assigned: "#57534A", picked_up: "#78756E",
  in_transit: "#1C1B1A", delivered: "#A09D95", disputed: "#1C1B1A", cancelled: "#C9C5B8",
};

export default function MapView({ jobs = [], drivers = [], route = null, endpoints = [], selectedId = null,
  onSelectJob, height = "100%", center = [10, 51], zoom = 4 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const driverMarkers = useRef({});
  const endpointMarkers = useRef([]);
  const routeFittedFor = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!TOKEN || !ref.current || mapRef.current) return;
    const map = new mapboxgl.Map({ container: ref.current, style: "mapbox://styles/mapbox/light-v11", center, zoom });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.on("load", () => {
      // Warm brand tint: cream land, soft blue water, gentle green parks
      (map.getStyle().layers || []).forEach((l) => {
        const id = l.id.toLowerCase();
        try {
          if (l.type === "background") map.setPaintProperty(l.id, "background-color", "#F6F3EA");
          else if (id.includes("water") && l.type === "fill") map.setPaintProperty(l.id, "fill-color", "#D9E7F3");
          else if ((id.includes("park") || id.includes("grass") || id.includes("wood")) && l.type === "fill") map.setPaintProperty(l.id, "fill-color", "#E2EBD6");
          else if (id.includes("landuse") && l.type === "fill") map.setPaintProperty(l.id, "fill-color", "#EFEAD9");
          else if (id === "land" && l.type === "fill") map.setPaintProperty(l.id, "fill-color", "#F6F3EA");
        } catch { /* paint mismatch on some layers */ }
      });
      map.addSource("jobs", { type: "geojson", data: { type: "FeatureCollection", features: [] }, cluster: true, clusterMaxZoom: 12, clusterRadius: 48 });
      map.addLayer({ id: "clusters", type: "circle", source: "jobs", filter: ["has", "point_count"],
        paint: { "circle-color": "#F2D06B", "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
          "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 } });
      map.addLayer({ id: "cluster-count", type: "symbol", source: "jobs", filter: ["has", "point_count"],
        layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 12 }, paint: { "text-color": "#1C1B1A" } });
      map.addLayer({ id: "job-points", type: "circle", source: "jobs", filter: ["!", ["has", "point_count"]],
        paint: { "circle-color": ["case", ["==", ["get", "id"], ["literal", selectedId || ""]], "#F2D06B", ["get", "color"]],
          "circle-radius": ["case", ["==", ["get", "id"], ["literal", selectedId || ""]], 9, 6.5],
          "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 } });
      map.on("click", "clusters", (e) => {
        const f = map.queryRenderedFeatures(e.point, { layers: ["clusters"] })[0];
        map.getSource("jobs").getClusterExpansionZoom(f.properties.cluster_id, (err, z) => {
          if (!err) map.easeTo({ center: f.geometry.coordinates, zoom: z });
        });
      });
      map.on("click", "job-points", (e) => {
        const f = e.features[0];
        if (!f) return;
        new mapboxgl.Popup({ offset: 16, closeButton: false })
          .setLngLat(f.geometry.coordinates)
          .setHTML(`<div style="font-family:'DM Sans',sans-serif;font-size:12px;line-height:1.55;padding:2px 4px"><strong>${f.properties.ref}</strong><br/>${f.properties.route}<br/><span style="color:#78756E">${(f.properties.status || "").replace(/_/g, " ")} · ${f.properties.price}</span></div>`)
          .addTo(map);
        if (f.properties.id && onSelectJob) onSelectJob(f.properties.id);
      });
      map.on("mouseenter", "job-points", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "job-points", () => (map.getCanvas().style.cursor = ""));
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const features = jobs
        .filter((j) => j.pickup?.lat && j.pickup?.lng)
        .map((j) => ({ type: "Feature",
          properties: { id: j.id, ref: j.ref, status: j.status, color: STATUS_COLORS[j.status] || "#1C1B1A",
            route: `${j.pickup?.city || "—"} → ${j.delivery?.city || "—"}`,
            price: j.price_net != null ? `${j.price_net} ${j.currency || "EUR"}` : "Open to bids" },
          geometry: { type: "Point", coordinates: [j.pickup.lng, j.pickup.lat] } }));
      const src = map.getSource("jobs");
      if (src) src.setData({ type: "FeatureCollection", features });
      if (map.getLayer("job-points")) {
        map.setPaintProperty("job-points", "circle-radius",
          ["case", ["==", ["get", "id"], selectedId || ""], 9, 6.5]);
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [jobs, selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      try {
        if (!route) {
          if (map.getSource("route")) { map.removeLayer("route"); map.removeSource("route"); }
          routeFittedFor.current = null;
          return;
        }
        const data = { type: "Feature", geometry: route, properties: {} };
        if (map.getSource("route")) map.getSource("route").setData(data);
        else {
          map.addSource("route", { type: "geojson", data });
          map.addLayer({ id: "route", type: "line", source: "route",
            paint: { "line-color": "#1C1B1A", "line-width": 4, "line-opacity": 0.85 } });
        }
        if (routeFittedFor.current !== route) {
          const coords = route.coordinates;
          if (coords?.length > 1) {
            const bounds = coords.reduce((b, c) => b.extend(c), new mapboxgl.LngLatBounds(coords[0], coords[0]));
            map.fitBounds(bounds, { padding: 60, duration: 600 });
          }
          routeFittedFor.current = route;
        }
      } catch { /* style not ready — retried on styledata/load */ }
    };
    apply();
    map.on("styledata", apply);
    map.on("load", apply);
    return () => { map.off("styledata", apply); map.off("load", apply); };
  }, [route]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    endpointMarkers.current.forEach((m) => m.remove());
    endpointMarkers.current = [];
    endpoints.forEach((ep, i) => {
      if (ep?.lat == null || ep?.lng == null) return;
      const el = document.createElement("div");
      el.style.cssText = `width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:700 12px 'DM Sans',sans-serif;border:2.5px solid #ffffff;box-shadow:0 1px 6px rgba(0,0,0,0.3);color:${i === 0 ? "#ffffff" : "#1C1B1A"};background:${i === 0 ? "#1C1B1A" : "#F2D06B"}`;
      el.textContent = i === 0 ? "A" : "B";
      endpointMarkers.current.push(new mapboxgl.Marker({ element: el }).setLngLat([ep.lng, ep.lat]).addTo(map));
    });
    return () => { endpointMarkers.current.forEach((m) => m.remove()); endpointMarkers.current = []; };
  }, [endpoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const live = new Set(drivers.map((d) => d.driver_id));
    drivers.forEach((d) => {
      let m = driverMarkers.current[d.driver_id];
      if (!m) {
        const el = document.createElement("div");
        el.style.cssText = "width:14px;height:14px;border-radius:50%;background:#1C1B1A;border:3px solid #F2D06B;box-shadow:0 1px 6px rgba(0,0,0,.35)";
        m = driverMarkers.current[d.driver_id] = new mapboxgl.Marker({ element: el })
          .setPopup(new mapboxgl.Popup({ offset: 18 }).setText(d.name || "Courier"))
          .addTo(map);
      }
      m.setLngLat([d.lng, d.lat]);
    });
    Object.keys(driverMarkers.current).filter((id) => !live.has(id)).forEach((id) => {
      driverMarkers.current[id].remove();
      delete driverMarkers.current[id];
    });
  }, [drivers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId || route) return;
    const j = jobs.find((x) => x.id === selectedId);
    if (j?.pickup?.lat && j?.pickup?.lng) {
      map.easeTo({ center: [j.pickup.lng, j.pickup.lat], zoom: Math.max(map.getZoom(), 7), duration: 600 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (mapRef.current) mapRef.current.resize();
  }, [fullscreen]);

  const fitAll = () => {
    const map = mapRef.current;
    if (!map) return;
    const pts = [
      ...jobs.filter((j) => j.pickup?.lat && j.pickup?.lng).map((j) => [j.pickup.lng, j.pickup.lat]),
      ...drivers.filter((d) => d.lat && d.lng).map((d) => [d.lng, d.lat]),
      ...endpoints.filter((e) => e?.lat != null && e?.lng != null).map((e) => [e.lng, e.lat]),
    ];
    if (pts.length === 0) return;
    if (pts.length === 1) return map.easeTo({ center: pts[0], zoom: 10 });
    const bounds = pts.reduce((b, c) => b.extend(c), new mapboxgl.LngLatBounds(pts[0], pts[0]));
    map.fitBounds(bounds, { padding: 70, maxZoom: 11, duration: 600 });
  };

  const locateMe = () => {
    const map = mapRef.current;
    if (!map || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => map.easeTo({ center: [p.coords.longitude, p.coords.latitude], zoom: 11, duration: 600 }),
      () => {}, { timeout: 5000 });
  };

  if (!TOKEN) {
    return (
      <div data-testid="map-unavailable" style={{ height }} className="flex items-center justify-center bg-[#EFECE2] rounded-[28px] text-sm text-[#78756E]">
        Map unavailable — Mapbox token not configured
      </div>
    );
  }

  const ctrlBtn = "w-9 h-9 bg-white/80 backdrop-blur-md border border-white/60 rounded-full shadow-sm flex items-center justify-center hover:bg-white transition-colors";
  return (
    <div className={fullscreen ? "fixed inset-0 z-50 bg-white" : "relative w-full rounded-[28px] overflow-hidden"}
      style={fullscreen ? {} : { height }} data-testid="map-wrapper">
      <div ref={ref} data-testid="map-view" className="w-full h-full" />
      <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5">
        <button type="button" data-testid="map-fit-btn" onClick={fitAll} title="Fit results" className={ctrlBtn}><Focus className="w-4 h-4" /></button>
        <button type="button" data-testid="map-locate-btn" onClick={locateMe} title="Locate me" className={ctrlBtn}><LocateFixed className="w-4 h-4" /></button>
        <button type="button" data-testid="map-fullscreen-btn" onClick={() => setFullscreen(!fullscreen)} title={fullscreen ? "Exit full screen" : "Full screen"} className={ctrlBtn}>
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export { STATUS_COLORS };
